import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, serverLogout } from '@/services/api';
import { getOverallStatus, onStatusChange, stopAllHubs, type ConnectionStatus } from '@/services/signalr';
import { toggleDarkMode, getTheme, setTheme, type ThemeName } from '@/shared/theme';
import { LeaderboardModal } from '@/features/interventions/LeaderboardPage';
import { GlobalSelectiveAlert } from '@/shared/GlobalSelectiveAlert';
import { InterventionInsertModal, type InterventionCategory } from '@/features/interventions/InterventionInsertModal';
import { gpsTracking } from '@/services/gpsTracking';
import { notifyNativeLogout } from '@/services/nativeBridge';
import { useAuth } from '@/shared/AuthContext';
import { ChangePasswordGate } from '@/features/auth/ChangePasswordGate';
import { AboutModal } from '@/shared/AboutModal';
import { APP_NAME, APP_VERSION, DEVELOPER } from '@/shared/appInfo';
import { REFETCH_DASHBOARD_MS } from '@/shared/timing';

interface NotificationBadges {
  pendingUsers: number;
  unreadChat: number;
  available: number;
  trainingMentions: number;
}

export function DashboardLayout(): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser: setAuthUser } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Init 'connecting' (non getOverallStatus(), che al primo render con mappa hub
  // vuota darebbe 'disconnected' -> pallino rosso fuorviante): la dashboard si
  // monta solo con sessione attiva, quindi si sta sempre connettendo. L'effect
  // onStatusChange sotto fa replay immediato dello stato reale appena montato.
  const [connStatus, setConnStatus] = useState<ConnectionStatus>('connecting');
  const [themeMenuOpen, setThemeMenuOpen] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [interventionInsert, setInterventionInsert] = useState<{ open: boolean; category: InterventionCategory }>({ open: false, category: null });

  function openInterventionInsert(category: InterventionCategory) {
    setInterventionInsert({ open: true, category });
    setSidebarOpen(false);
  }

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    // Riprende GPS tracking selettiva dopo refresh, se attivo
    gpsTracking.resumeFromStorage();
  }, [navigate, user]);

  useEffect(() => { setSidebarOpen(false); setThemeMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    const off = onStatusChange(() => setConnStatus(getOverallStatus()));
    return off;
  }, []);

  const { data: badges } = useQuery<NotificationBadges>({
    queryKey: ['dashboard.badges'],
    queryFn: async () => (await api.get('/dashboard/badges/notifications')).data,
    enabled: !!user,
    refetchInterval: REFETCH_DASHBOARD_MS,
  });

  const currentYear = new Date().getFullYear();
  const { data: yearStats } = useQuery<Array<{ year: number; count: number }>>({
    queryKey: ['interventions.stats.yearly', user?.id],
    queryFn: async () => (await api.get('/interventions/stats/yearly')).data,
    enabled: !!user,
  });
  const { data: monthStats } = useQuery<Array<{ month: number; count: number }>>({
    queryKey: ['interventions.stats.monthly', user?.id, currentYear],
    queryFn: async () => (await api.get('/interventions/stats/monthly', { params: { year: currentYear } })).data,
    enabled: !!user,
  });

  const monthNames = ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'];

  async function logout() {
    stopAllHubs();
    // Revoca lato server (invalida refresh + cancella cookie httpOnly) + pulizia
    // locale (access in memoria/localStorage). Best-effort: prosegue anche offline.
    await serverLogout();
    // setAuthUser(null) rimuove vvf.user da localStorage -> propaga ai useAuth
    // consumer (audit #8) E notifica gli altri tab (storage event -> logout).
    setAuthUser(null);
    notifyNativeLogout();
    navigate('/login');
  }

  if (!user) return <div className="spinner-overlay" />;

  // F19: password temporanea da import → cambio obbligatorio PRIMA di qualsiasi
  // navigazione (il gate copre tutte le route figlie dell'Outlet).
  if (user.mustChangePassword === true) return <ChangePasswordGate />;

  const hasUserRole = (r: string) => (user.roles ?? []).includes(r);
  const canAdmin = hasUserRole('Amministratore') || hasUserRole('SuperAdmin');
  const canMagazzino = hasUserRole('Magazzino') || canAdmin;
  const canHr = hasUserRole('RisorseUmane') || canAdmin;
  // canTecnico per uso futuro (idranti admin actions)
  void (hasUserRole('TecnicoIdranti') || canAdmin);

  // Un utente "operativo" e' un pompiere: vede dashboard interventi, disponibilita',
  // armadietto, classifiche, chat. Il SuperAdmin di sistema (senza ruolo Utente) e'
  // un sistemista puro: niente voci operative, solo amministrazione globale.
  const isOperativeUser = hasUserRole('Utente') || hasUserRole('Magazzino')
    || hasUserRole('TecnicoIdranti') || hasUserRole('RisorseUmane') || hasUserRole('Amministratore');

  const statusLabel = connStatus === 'connected' ? 'Online' : connStatus === 'connecting' ? 'Connessione...' : 'Offline';

  return (
    <>
      <GlobalSelectiveAlert />
      <header className="app-header">
        <div className="app-header-left">
          <button className="hamburger-btn" onClick={() => setSidebarOpen(!sidebarOpen)}
            aria-label="Apri menu">☰</button>
          <div onClick={() => navigate('/dashboard')}
            style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}
            title="Vai alla dashboard">
            <img src="/icon-48.webp" alt="" style={{ width: 32, height: 32, borderRadius: 6 }} />
            <h2 style={{ margin: 0 }}>VVF Tracker</h2>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span title={statusLabel} style={{ color: 'white', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, lineHeight: 1 }}>
            <span className={`connection-dot ${connStatus}`} />
            <span className="app-header-nickname">{statusLabel}</span>
          </span>
          <span className="app-header-nickname" style={{ color: 'white', fontSize: 14, opacity: 0.9, lineHeight: 1 }}>
            {user.nickname}
          </span>
        </div>
      </header>

      {themeMenuOpen && <ThemeColorModal onClose={() => setThemeMenuOpen(false)} />}
      {showLeaderboard && <LeaderboardModal onClose={() => setShowLeaderboard(false)} />}
      {showAbout && <AboutModal onClose={() => setShowAbout(false)} />}

      {sidebarOpen && <div className="sidebar-overlay active" onClick={() => setSidebarOpen(false)} />}

      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        {/* === Ordine 1:1 dalla legacy index.html ============================
              1) Inserisci Intervento (verde)
              2) Quick-insert grid: Incendio / Soccorso / Incidente / Altro
              3) Stats anno+mese (testo informativo)
              4) Gestione Utenti, Scadenze Utenti (orange-light flat UI)
              5) Magazzino, Mezzi, Distaccamenti, Documenti, Mappa Idranti (orange Bootstrap)
              6) Ricerca Persona (red-dark)
              7) Disponibilita, Statistiche, Addestramento, Impostazioni (red Bootstrap)
              8) Armadietto, Grafici, Classifica (amber)
              9) Inserimento Manuale (blue Bootstrap)
              10) Chat (green) + VPN links
           ============================================================ */}

        {isOperativeUser && (
          <>
            <button type="button" onClick={() => openInterventionInsert(null)}
              className="color-green sidebar-cta">
              ➕ Inserisci Intervento
            </button>
            <div className="sidebar-quick-grid">
              <button type="button" onClick={() => openInterventionInsert('Incendio')} className="color-red sidebar-quick">🔥 Incendio</button>
              <button type="button" onClick={() => openInterventionInsert('Soccorso')} className="color-teal sidebar-quick">🚑 Soccorso</button>
              <button type="button" onClick={() => openInterventionInsert('Incidente')} className="color-amber sidebar-quick">🚗 Incidente</button>
              <button type="button" onClick={() => openInterventionInsert('Altro')} className="color-gray sidebar-quick">📝 Altro</button>
            </div>

            <div className="sidebar-stats">
              <div className="sidebar-stats-title">Interventi per anno:</div>
              {(yearStats && yearStats.length > 0
                ? yearStats
                : [{ year: currentYear, count: 0 }]
              ).map(y => (
                <div key={y.year} className="sidebar-stats-row">
                  <span className="label">{y.year}:</span>
                  <span className="value">{y.count}</span>
                </div>
              ))}
            </div>

            <div className="sidebar-stats">
              <div className="sidebar-stats-title">Interventi per mese ({currentYear}):</div>
              {(() => {
                const currentMonth = new Date().getMonth() + 1; // 1-12
                // Mostra: il mese corrente sempre + tutti gli altri mesi con almeno 1 intervento
                const rows = (monthStats ?? []).filter(m => m.count > 0 || m.month === currentMonth);
                if (rows.length === 0) {
                  return (
                    <div className="sidebar-stats-row">
                      <span className="label">{monthNames[currentMonth - 1]}:</span>
                      <span className="value">0</span>
                    </div>
                  );
                }
                return rows.map(m => (
                  <div key={m.month} className="sidebar-stats-row">
                    <span className="label" style={m.month === currentMonth ? { fontWeight: 700, color: '#28a745' } : undefined}>
                      {monthNames[m.month - 1]}{m.month === currentMonth ? ' (in corso)' : ''}:
                    </span>
                    <span className="value">{m.count}</span>
                  </div>
                ));
              })()}
            </div>
          </>
        )}

        {/* Gestione utenti / scadenze — orange-light flat UI come legacy */}
        {canAdmin && (
          <NavLink to="/admin" className={({ isActive }) => `color-orange-light ${isActive ? 'active' : ''}`}>
            👥 Gestione Utenti
            {badges?.pendingUsers ? <span className="sidebar-badge warn">{badges.pendingUsers}</span> : null}
          </NavLink>
        )}
        {canHr && <NavLink to="/deadlines" className={({ isActive }) => `color-orange-light ${isActive ? 'active' : ''}`}>📅 Scadenze Utenti</NavLink>}

        {/* Gestione operativa — orange Bootstrap come legacy */}
        {canMagazzino && <NavLink to="/warehouse" className={({ isActive }) => `color-orange ${isActive ? 'active' : ''}`}>🏭 Gestione Magazzino</NavLink>}
        {isOperativeUser && <NavLink to="/vehicles" className={({ isActive }) => `color-orange ${isActive ? 'active' : ''}`}>🚒 Gestione Mezzi</NavLink>}
        {canAdmin && <NavLink to="/my-tenant" className={({ isActive }) => `color-orange ${isActive ? 'active' : ''}`}>🏢 Mio Distaccamento</NavLink>}
        {hasUserRole('SuperAdmin') && <NavLink to="/tenants" className={({ isActive }) => `color-orange ${isActive ? 'active' : ''}`}>🏢 Gestione Distaccamenti</NavLink>}
        {isOperativeUser && <NavLink to="/documents" className={({ isActive }) => `color-orange ${isActive ? 'active' : ''}`}>📄 Documenti</NavLink>}
        <NavLink to="/hydrants" className={({ isActive }) => `color-orange ${isActive ? 'active' : ''}`}>💧 Mappa Idranti</NavLink>

        {/* Ricerca Persona — feature IN SVILUPPO: voce disabilitata, testo barrato
            + icona lavori in corso. La route /sar e' anche reindirizzata. */}
        {isOperativeUser && (
          <button type="button" className="color-red-dark" disabled
            title="Funzione in sviluppo — disponibile a breve"
            style={{ opacity: 0.6, cursor: 'not-allowed' }}>
            🚧 <span style={{ textDecoration: 'line-through' }}>Ricerca Persona</span>
          </button>
        )}

        {/* Disponibilita / Statistiche / Addestramento — red Bootstrap
            (Selettive non e' nella sidebar: gestite tramite popup dashboard) */}
        {isOperativeUser && <>
          <NavLink to="/availability" className={({ isActive }) => `color-red ${isActive ? 'active' : ''}`}>
            📅 Disponibilita
            {badges?.available ? <span className="sidebar-badge">{badges.available}</span> : null}
          </NavLink>
          <NavLink to="/interventions" className={({ isActive }) => `color-red ${isActive ? 'active' : ''}`}>📈 Statistiche Interventi</NavLink>
          <NavLink to="/selective-stats" className={({ isActive }) => `color-red ${isActive ? 'active' : ''}`}>📊 Statistiche Selettive</NavLink>
          <NavLink to="/training" className={({ isActive }) => `color-red ${isActive ? 'active' : ''}`}>
            📋 Addestramento
            {badges?.trainingMentions ? <span className="sidebar-badge warn">{badges.trainingMentions}</span> : null}
          </NavLink>
          <NavLink to="/settings" className={({ isActive }) => `color-red ${isActive ? 'active' : ''}`}>⚙️ Impostazioni</NavLink>
        </>}

        {/* Personale — amber */}
        {isOperativeUser && <>
          <NavLink to="/locker" className={({ isActive }) => `color-amber ${isActive ? 'active' : ''}`}>👤 Armadietto Personale</NavLink>
          <NavLink to="/charts" className={({ isActive }) => `color-amber ${isActive ? 'active' : ''}`}>📊 Grafici</NavLink>
          <button type="button" onClick={() => { setShowLeaderboard(true); setSidebarOpen(false); }}
            className="color-amber" style={{ textAlign: 'left' }}>
            🏆 Classifica
          </button>
        </>}

        {/* Inserimento Manuale rimosso (Bryan): i pulsanti quick-insert
            permettono gia' di specificare data e ora. */}

        {/* Chat — verde */}
        {isOperativeUser && (
          <NavLink to="/chat" className={({ isActive }) => `color-green ${isActive ? 'active' : ''}`}>
            💬 Chat
            {badges?.unreadChat ? <span className="sidebar-badge danger">{badges.unreadChat}</span> : null}
          </NavLink>
        )}

        {/* Bryan: separatore prima della sezione siti esterni VVF */}
        <hr className="sidebar-divider" />

        {/* Link Esterni VPN (URL ufficiali dipvvf forniti da Bryan) */}
        <a href="https://gacweb.dipvvf.it/gac-web/" target="_blank" rel="noreferrer" className="color-orange">🔗 GAC Web</a>
        <a href="https://statri-web.dipvvf.it" target="_blank" rel="noreferrer" className="color-blue">🔗 STATRI-Web</a>
        <a href="https://pacweb.dipvvf.it/track/" target="_blank" rel="noreferrer" className="color-purple">🔗 TRACK</a>

        {/* Bryan: separatore prima della sezione preferenze utente */}
        <hr className="sidebar-divider" />

        {/* Tema + Dark Mode + Logout (stile legacy). Il toggle "Resta collegato"
            che stava qui era un residuo legacy SCOLLEGATO (scriveva
            localStorage vvf.remember che nessuno legge): la scelta reale e' il
            checkbox "Ricordami" al login (refresh token 7 vs 30 giorni). */}
        <button type="button" onClick={() => setThemeMenuOpen(o => !o)} className="sidebar-link-light">
          🎨 Tema Colore
        </button>
        <button type="button" onClick={toggleDarkMode} className="sidebar-link-light">
          🌙 Dark Mode
        </button>
        <button type="button" onClick={logout} className="sidebar-link-light">
          🚪 Logout
        </button>
      </aside>

      <InterventionInsertModal
        open={interventionInsert.open}
        category={interventionInsert.category}
        onClose={() => setInterventionInsert({ open: false, category: null })}
      />

      <main className="main-content">
        <div className="page-container page-fade" key={location.pathname}>
          <Outlet />
        </div>
        <footer className="app-footer">
          {/* Click -> modal About con i riferimenti dello sviluppatore. */}
          <button type="button" className="app-footer-link" onClick={() => setShowAbout(true)}>
            {APP_NAME} v{APP_VERSION} · {DEVELOPER.name}
          </button>
        </footer>
      </main>
    </>
  );
}

interface ThemeOption { value: ThemeName; label: string; color: string }
const THEME_OPTIONS: ThemeOption[] = [
  { value: 'verde', label: 'Verde VVF (Default)', color: '#28a745' },
  { value: 'rosso', label: 'Rosso VVF', color: '#dc3545' },
  { value: 'blu', label: 'Blu Professionale', color: '#0d6efd' },
];

function ThemeColorModal({ onClose }: { onClose: () => void }): React.JSX.Element {
  const current = getTheme();
  function choose(t: ThemeName): void { setTheme(t); onClose(); }
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 380, padding: 24 }}>
        <h3 style={{ textAlign: 'center', margin: '0 0 20px' }}>🎨 Scegli Tema Colore</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {THEME_OPTIONS.map(o => (
            <button key={o.value} onClick={() => choose(o.value)}
              style={{
                display: 'flex', alignItems: 'center', gap: 14, padding: 12,
                background: '#fff', border: current === o.value ? `2px solid ${o.color}` : '2px solid #e9ecef',
                borderRadius: 10, cursor: 'pointer', fontSize: 15, fontWeight: 600,
                boxShadow: current === o.value ? `0 0 0 3px ${o.color}33` : 'none',
              }}>
              <span style={{
                width: 40, height: 40, borderRadius: 8, background: o.color,
                boxShadow: '0 2px 6px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
              }} />
              <span>{o.label}</span>
            </button>
          ))}
        </div>
        <button onClick={onClose}
          style={{ marginTop: 18, width: '100%', padding: 12, border: 'none', borderRadius: 8, cursor: 'pointer',
                   background: '#6c757d', color: 'white', fontWeight: 700 }}>
          Chiudi
        </button>
      </div>
    </div>
  );
}
